import { SwatchGroup } from '@corratech/pylot-swatches'
import type { ConfigurableVariant } from '@pylot-data/pylotschema'
import cn from 'classnames'
import React, { ReactElement } from 'react'
import type { UseOptionSelectorReturn } from '../hooks/useOptionSelector'
import { getDisabledOptionValues } from '../utils/getDisabledOptionValues'
import s from './VariantSelector.module.scss'
import { VariantCount } from '../VariantCount'

export type VariantSelectorProps = UseOptionSelectorReturn & {
    disableAll: boolean
    variants: ConfigurableVariant[]
    showVariantCount?: boolean
    displaySwatchOptionLabel?: boolean
    setImage?: React.Dispatch<React.SetStateAction<string>>
    colorsImagesMap?: Map<string, string>
    displayTallaSwatch?: boolean
    displaySwatchLabel?: boolean
    restriction?: number
    productURL?: string
    /**
     * Muestra las opciones sin stock en estado deshabilitado en vez de ocultarlas.
     * Activo en la PDP (EVO-245); apagado en tiles y carrito.
     */
    showUnavailableOptions?: boolean
}

/**
 * Tells which attributes should be displayed as circular.
 * Hardcoded array to match previous designs.
 */
const circularAttributeCodes = ['size']

/**
 * Component that displays attributes and swatches.
 * Should be used in combination with `useOptionSelector` hook.
 * @param param0 product option API, that `useOptionSelector` hook provides.
 * @param param0.disableAll boolean value that indicates if parent product is *OUT_OF_STOCK*
 * @param param0.variants configurable product variants. Disabled options are calculated based on variant attributes.
 * @returns Varian selector representation (attributes and swatches)
 */
const VariantSelector = ({
    options,
    selectedOptions,
    setSelectedOptions,
    disableAll = false,
    showVariantCount = false,
    displaySwatchOptionLabel = false,
    variants = [],
    setImage,
    colorsImagesMap,
    displayTallaSwatch = true,
    displaySwatchLabel = true,
    restriction,
    productURL,
    showUnavailableOptions = false
}: VariantSelectorProps): ReactElement => {
    const disabledValues = getDisabledOptionValues(
        options,
        selectedOptions,
        variants
    )

    return (
        <div>
            {options.map((opt, idx: number) => {
                const disabledValuesForOption = disabledValues?.[opt.code] ?? []
                return (
                    <div
                        className={cn({
                            ['pb-4']: idx !== options.length - 1
                        })}
                        key={opt.code}
                    >
                        {showVariantCount && (
                            <VariantCount
                                values={
                                    opt.values?.filter(
                                        (v) =>
                                            !disabledValuesForOption.includes(
                                                v.label!
                                            )
                                    ) ?? null
                                }
                                displayName={opt.displayName}
                            />
                        )}
                        {opt.displayName && displaySwatchLabel && (
                            <div
                                className={cn(
                                    'pdp-swatch-label',
                                    s['display-name']
                                )}
                            >
                                {opt.displayName}{' '}
                                <span className="font-bold">
                                    {selectedOptions[opt.code]}
                                </span>
                            </div>
                        )}

                        <SwatchGroup
                            option={opt}
                            disableAll={disableAll}
                            disabledValues={disabledValuesForOption}
                            isCircle={circularAttributeCodes.includes(opt.code)}
                            selectedOptions={selectedOptions}
                            setSelectedOptions={setSelectedOptions}
                            displaySwatchOptionLabel={displaySwatchOptionLabel}
                            setImage={setImage}
                            colorsImagesMap={colorsImagesMap}
                            displayTallaSwatch={displayTallaSwatch}
                            restriction={restriction}
                            productURL={productURL}
                            showUnavailableOptions={showUnavailableOptions}
                        />
                    </div>
                )
            })}
        </div>
    )
}

export default VariantSelector
